import React, { useRef, useState } from "react";
import { NextSeo } from "next-seo";
import { Nav } from "@/components/navigation/Navbar";
import Crumbs from "@/components/navigation/Crumbs";
import * as Text from "@/components/text";
import qs from "qs";
import apiClient from "@/utils/apiClient";
import ProjectHistory from "@/components/initiatives/ProjectHistory";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Navigation, Thumbs } from "swiper/modules";
import { ArrowLeftIcon, ArrowRightIcon } from "@heroicons/react/outline";
import Image from "next/image";
import Button from "@/components/actions/Button";
import Link from "next/link";
import useWindowSize from "@/hooks/useWindowSize";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

export default function SpecialProjects({ mediaCenter }) {
  const afterRef = useRef(null);
  const paginationRef = useRef(null);
  const beforeRef = useRef(null);
  const [slideIndex, setIndex] = useState(0);
  const windowSize = useWindowSize();

  return (
    <div>
      <div className="amb-container">
        <Nav className="bg-gray-100 grow" theme="light" />
        <NextSeo
          title="Proyectos Especiales - Ambulante"
          description="Información general Coordinación de comunicación Sofía Llorente / slz@ambulante.org Coordinación de prensa Icunacury Acosta / prensa@ambulante.org Boletines 2023 Ambulante Gira de Documentales revela la imagen de su decimoctava edición Anunciamos el arranque de Ambulante Presenta 2023 Materiales Ambulante Presenta 2023 Catálogo Ambulante Presenta 2023 Guía de discusión Las colonias Programas de mano ediciones pasadas Ambulante"
          canonical={`https://ambulante.org/proyectos-especiales}`}
          openGraph={{
            url: `https://ambulante.org/proyectos-especiales`,
            title: "Proyectos especiales - Ambulante",
            description:
              "Información general Coordinación de comunicación Sofía Llorente / slz@ambulante.org Coordinación de prensa Icunacury Acosta / prensa@ambulante.org Boletines 2023 Ambulante Gira de Documentales revela la imagen de su decimoctava edición Anunciamos el arranque de Ambulante Presenta 2023 Materiales Ambulante Presenta 2023 Catálogo Ambulante Presenta 2023 Guía de discusión Las colonias Programas de mano ediciones pasadas Ambulante",
            site_name: "Ambulante",
          }}
        />
        <div className="flex flex-row gap-2 py-4">
          <Crumbs
            breadcrumbs={[
              { href: "/", text: "Inicio" },
              {
                href: "/iniciativas/gira?seccion=las-peliculas",
                text: "Programas",
              },
            ]}
          />
          <Text.NavigationLabel className="mt-0.5 text-xs font-thin text-neutral-600 md:mt-1.5 md:text-base md:font-normal ">
            Proyectos especiales
          </Text.NavigationLabel>
        </div>
        <div className="w-full mt-8 font-bold text-center md:mb-8 lg:mt-20">
          <Text.Headline
            as="p"
            className="mb-4 text-[36px] md:text-[40px] lg:text-[60px]"
          >
            {mediaCenter?.title}
          </Text.Headline>
          <Text.Headline
            as="p"
            className="text-[24px] font-light md:pt-3 lg:text-[40px]"
          >
            {mediaCenter?.subtitle}
          </Text.Headline>
        </div>
      </div>
      {mediaCenter?.slider.length > 0 ? (
        <div className="relative overflow-hidden sm:space-y-20 md:space-y-12">
          <div className="relative space-y-4 sm:px-0 lg:px-24 xl:mx-auto xl:max-w-[1440px]">
            <Swiper
              spaceBetween={windowSize.width <= 900 ? 20 : 10}
              loop={true}
              onActiveIndexChange={({ realIndex }) => {
                setIndex(realIndex);
              }}
              slidesPerView={windowSize.width <= 900 ? 1.15 : 1}
              modules={[Thumbs, Navigation, Pagination]}
              centeredSlides={true}
              pagination={{
                clickable: true,
                type: "bullets",
                el: paginationRef.current,
                currentClass: "bg-neutral-900",
                bulletClass: "block w-6 h-1 bg-gray-200 ",
                bulletActiveClass: "bg-neutral-900",
              }}
              navigation={{
                enabled: true,
                nextEl: ".after-button",
                prevEl: ".before-button",
                hiddenClass: "hidden",
                disabledClass: "text-neutral-50 border-neutral-50",
              }}
            >
              {mediaCenter?.slider.map((slide, i) => {
                return (
                  <SwiperSlide key={i} className="flex justify-center w-full">
                    <div className="flex w-full sm:h-[416px] md:h-[352px]">
                      <div className="absolute flex w-full h-full">
                        <Image
                          src={slide?.background_image?.data?.attributes?.url}
                          alt={`${slideIndex}`}
                          width={1208}
                          height={352}
                          className="object-cover w-full h-full"
                        />
                        <div className="absolute w-full h-full z-1 bg-gradient-to-t from-black via-transparent to-transparent"></div>
                      </div>
                      <div className="flex items-end justify-start h-full text-5xl text-white sm:ml-6 md:ml-16 lg:ml-36">
                        <div className="z-50 sm:max-w-[290px] md:max-w-[588px]">
                          <Text.Headline
                            as="p"
                            className="text-[14px] font-normal uppercase"
                          >
                            {slide?.header_text}
                          </Text.Headline>
                          <Text.Headline as="p" className="mb-2 text-[28px]">
                            {slide?.title}
                          </Text.Headline>
                          <Text.Headline
                            as="p"
                            className="mb-6 text-[16px] font-normal leading-[22px]"
                          >
                            {slide?.description}
                          </Text.Headline>
                          <Link passHref href={slide?.link} className="w-full">
                            <Button
                              size="sm"
                              color="primary-dark"
                              label="Ver más detalles"
                              className="px-4 mb-8 w-fit"
                            ></Button>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </SwiperSlide>
                );
              })}
            </Swiper>
            <div className="justify-center mt-0 sm:flex md:flex lg:my-0">
              <div className="z-50 flex items-center justify-center gap-5 align-middle">
                <button
                  ref={beforeRef}
                  className="before-button absolute left-8 top-[152px] hidden bg-black/20  p-4 text-white/90  lg:block"
                >
                  <ArrowLeftIcon className="w-4 h-4 " />
                </button>
                <div className="flex gap-5" ref={paginationRef}></div>
                <button
                  ref={afterRef}
                  className="after-button z- absolute right-8 top-[152px] hidden bg-black/20 p-4 text-white/90  lg:block"
                >
                  <ArrowRightIcon className="w-4 h-4 " />
                </button>
              </div>
            </div>
          </div>
          <div className="absolute top-0 h-[256px] w-full bg-proyectos-especiales lg:h-[256px] xl:w-[100vw]"></div>
        </div>
      ) : (
        <div className="w-full h-4 border-b-4 border-proyectos-especiales"></div>
      )}
      <div className="flex justify-center px-16 my-8 bg-neutral-50 md:my-10">
        <Text.Headline
          as="p"
          className="px-4 py-12 text-center text-[24px] font-light lg:max-w-[846px] lg:text-[32px]"
        >
          {mediaCenter?.banner_text}
        </Text.Headline>
      </div>
      {mediaCenter?.project && <ProjectHistory data={mediaCenter?.project} />}
    </div>
  );
}

export async function getServerSideProps() {
  const queryParams = qs.stringify({
    populate: {
      project: {
        populate: {
          image: "*",
          entrada: "*",
        },
      },
      slider: {
        populate: {
          background_image: "*",
        },
      },
    },
  });

  const { data } = await apiClient.get(`/api/proyecto-especial?${queryParams}`);

  return {
    props: {
      mediaCenter: data.data.attributes,
    },
  };
}
